import useSWRInfinite from "swr/infinite";
import { getBoardsForWorkspace, getMondayItemsForBoard, getMondayWorkspaces } from "frontend/api";
import { useEffect, useMemo, useState } from "react";
import { MondayWorkspaces, MondayWorkspacesWithCursor } from "shared/datamodel/schemas/monday-workspace";
import {
  MondayItemType,
  ResourceType,
} from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import { prop, uniqBy } from "rambda";
import { useMondayImportItemsContext } from "frontend/canvas-designer-new/monday-import-items/monday-import-items-context";
import { useLocalStorage } from "react-use";
import { useMondayContext } from "frontend/context/monday-context";
import { isCIM } from "utils/url-utils";
import { getWorkspacesForUserUsingSDK } from "frontend/services/monday/workspaces";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useDebounce } from "usehooks-ts";

type MondayItem = {
  id: string;
  name: string;
  board: { id: string };
  subitems: Exclude<MondayItem, "group">[];
  group?: { id: string; title: string; color: string };
  column_values?: Array<{ id: string; type: string; text?: string; value?: string }>;
};

const formatWorkspaces = (workspaces: MondayWorkspaces): ResourceType[] => {
  const allWorkspaces = workspaces
    .filter((w) => !!w.name)
    .map((workspace) => ({
      id: workspace.id,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: workspace.name!,
      image: workspace.settings?.icon?.image ?? undefined,
      color: workspace.settings?.icon?.color ?? undefined,
    }));

  return uniqBy(prop("id"), allWorkspaces);
};

const formatItems = (
  items: MondayItem[] = []
): { id: string; name: string; color: string; items: MondayItemType[] }[] => {
  // Group items by their group id and aggregate items under each group
  const groupsMap: { [groupId: string]: { id: string; name: string; color: string; items: MondayItemType[] } } = {};

  for (const item of items) {
    const groupId = item?.group?.id;
    if (!groupsMap[groupId ?? ""]) {
      if (!item.group) {
        continue;
      }
      groupsMap[groupId ?? ""] = {
        id: groupId ?? "",
        name: item.group.title,
        color: item.group.color,
        items: [],
      };
    }
    groupsMap[groupId ?? ""].items.push({
      id: item.id,
      name: item.name,
      subItems: item.subitems,
      column_values: item.column_values,
    });
  }

  return Object.values(groupsMap);
};

const buildWorkspacesKey = (page: number | string) => {
  const params = new URLSearchParams({ page: page.toString() });
  return `workspaces?${params.toString()}`;
};

const parseWorkspacesKey = (key: string) => {
  const [, queryString] = key.split("?");
  const params = new URLSearchParams(queryString);
  return {
    page: Number(params.get("page")) || 0,
  };
};

const buildBoardsKey = (
  workspaceId: string,
  page: number,
  withColumns: boolean,
  withSubItemColumns: boolean,
  search?: string
) => {
  const params = new URLSearchParams({
    workspaceId,
    page: page.toString(),
    withColumns: withColumns.toString(),
    withSubItemColumns: withSubItemColumns.toString(),
  });
  if (search) {
    params.set("search", search);
  }
  return `boards?${params.toString()}`;
};

const parseBoardsKey = (key: string) => {
  const [, queryString] = key.split("?");
  const params = new URLSearchParams(queryString);
  return {
    workspaceId: params.get("workspaceId") ?? "",
    page: Number(params.get("page")) || 1,
    withColumns: params.get("withColumns") === "true",
    withSubItemColumns: params.get("withSubItemColumns") === "true",
    search: params.get("search") ?? undefined,
  };
};

const buildItemsKey = (boardId: string, cursor: string | undefined, withColumnValues: boolean) => {
  const params = new URLSearchParams({
    boardId,
    withColumnValues: withColumnValues.toString(),
  });
  if (cursor) {
    params.set("cursor", cursor);
  }
  return `items?${params.toString()}`;
};

const parseItemsKey = (key: string) => {
  const [, queryString] = key.split("?");
  const params = new URLSearchParams(queryString);
  return {
    boardId: params.get("boardId") ?? "",
    cursor: params.get("cursor") ?? undefined,
    withColumnValues: params.get("withColumnValues") === "true",
  };
};

const useWorkspaces = () => {
  const useMondaySdk = useFeatureFlag("use-monday-sdk");

  const getKey = (index: number, previousData: MondayWorkspacesWithCursor) => {
    const page = previousData?.cursor ?? index;
    return buildWorkspacesKey(page);
  };

  const { data, isLoading, isValidating, error, setSize, size } = useSWRInfinite<MondayWorkspacesWithCursor>(
    getKey,
    async (key) => {
      const { page: pageNumber } = parseWorkspacesKey(key);
      if (useMondaySdk && isCIM()) {
        return await getWorkspacesForUserUsingSDK({ page: pageNumber });
      }
      return await getMondayWorkspaces({ page: pageNumber });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      keepPreviousData: true,
      revalidateFirstPage: false,
    }
  );

  const formattedWorkspaces = useMemo(
    () => formatWorkspaces((data ?? []).flatMap((result) => result.workspaces) ?? []),
    [data]
  );

  const loadMoreWorkspaces = () => {
    setSize(size + 1);
  };

  return {
    data: formattedWorkspaces,
    isLoading: isLoading || isValidating,
    isInitialLoading: isLoading,
    error,
    loadMoreWorkspaces,
  };
};

const useBoards = (
  selectedWorkspace: ResourceType | null,
  withColumns: boolean = false,
  withSubItemColumns: boolean = false,
  searchTerm?: string
) => {
  const getKey = (index: number, previousData: { boards: { id: string; name: string }[]; page?: number }) => {
    if (!selectedWorkspace) {
      return null;
    }
    const page = previousData?.page ?? index;
    return buildBoardsKey(selectedWorkspace.id, page, withColumns, withSubItemColumns, searchTerm);
  };

  const { data, isLoading, isValidating, error, setSize, size, mutate } = useSWRInfinite<{
    boards: {
      id: string;
      name: string;
      type: string;
      parentId?: string;
      columns?: { id: string; type: string; title: string }[];
    }[];
    page?: number;
  }>(
    getKey,
    async (key) => {
      const params = parseBoardsKey(key);
      return await getBoardsForWorkspace({
        workspaceId: params.workspaceId,
        page: params.page,
        withColumns: params.withColumns,
        withSubItemColumns: params.withSubItemColumns,
        searchTerm: params.search,
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      keepPreviousData: true,
      revalidateFirstPage: false,
    }
  );

  useEffect(() => {
    if (size > 1) {
      setSize(1);
      mutate();
    }
  }, [searchTerm, setSize, size, mutate]);

  const formattedBoards = useMemo(() => {
    const flatData = (data ?? []).flatMap((result) => result.boards) ?? [];

    return {
      boards: uniqBy(
        prop("id"),
        flatData.filter((board) => board.type === "board")
      ),
      subItemsBoards: uniqBy(
        prop("id"),
        flatData.filter((board) => board.type === "sub_items_board")
      ),
    };
  }, [data]);

  const loadMoreBoards = () => {
    if (data?.[0]?.page === -1) {
      return;
    }
    setSize(size + 1);
  };

  return {
    boards: formattedBoards.boards,
    subItemsBoards: formattedBoards.subItemsBoards,
    isLoading: isLoading || isValidating,
    isInitialLoading: isLoading,
    error,
    loadMoreBoards,
  };
};

const useItems = (selectedBoard: ResourceType | null, withColumnValues: boolean = false) => {
  const getKey = (_: number, previousData: { items: MondayItem[]; cursor?: string }) => {
    if (!selectedBoard) {
      return null;
    }
    return buildItemsKey(selectedBoard.id, previousData?.cursor, withColumnValues);
  };

  const { data, isLoading, isValidating, error, setSize, size } = useSWRInfinite<{
    items: MondayItem[];
    cursor?: string;
  }>(
    getKey,
    async (key) => {
      const params = parseItemsKey(key);
      return await getMondayItemsForBoard({
        boardId: params.boardId,
        cursor: params.cursor,
        withColumnValues: params.withColumnValues,
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: true,
      keepPreviousData: true,
      revalidateFirstPage: false,
      revalidateOnMount: true,
    }
  );

  const loadMoreItems = () => {
    setSize(size + 1);
  };

  const formattedItems = useMemo(() => formatItems(data?.flatMap((result) => result.items) ?? []), [data]);

  const itemIdToBoardId = useMemo(() => {
    // Now stores { boardId, isSubItem } for each itemId
    const mapItemsRecursive = (
      items: any[] = [],
      acc: Record<string, { boardId: string; isSubItem: boolean }> = {},
      isSubItem: boolean = false
    ) => {
      for (const item of items) {
        if (item?.id && item?.board?.id) {
          acc[item.id] = { boardId: item.board.id, isSubItem };
        }
        if (item?.subitems && Array.isArray(item.subitems) && item.subitems.length > 0) {
          mapItemsRecursive(item.subitems, acc, true);
        }
      }
      return acc;
    };

    const allItems: any[] = (data ?? []).flatMap((result) => result.items);
    return mapItemsRecursive(allItems);
  }, [data]);

  const hasMore = useMemo(() => {
    return !data?.some((result) => !result.cursor);
  }, [data]);

  return {
    data: formattedItems,
    itemIdToBoardId,
    hasMore,
    isLoading: isLoading || isValidating,
    isInitialLoading: isLoading,
    error,
    loadMoreItems,
  };
};

export type BoardWithColumns = {
  id: string;
  name: string;
  type: string;
  columns?: { id: string; type?: string; title?: string; settings_str?: string }[];
};

export type BoardCompatibilityFilter = (board: BoardWithColumns) => boolean;

export type BoardWithCompatibility = {
  id: string;
  name: string;
  isCompatible: boolean;
};

export const useMondayImportItems = (options?: {
  withColumns?: boolean;
  withColumnValues?: boolean;
  withSubItemColumns?: boolean;
  boardCompatibilityFilter?: BoardCompatibilityFilter;
}) => {
  const {
    withColumns = false,
    withColumnValues = false,
    withSubItemColumns = false,
    boardCompatibilityFilter,
  } = options ?? {};

  const workspaces = useWorkspaces();
  const [lastUsedIds, setLastUsedIds] = useLocalStorage<string | null>("last-used-workspace-board-id", null);
  const { contextData } = useMondayContext();
  const contextKey = useMemo(() => {
    const boardViewContext = contextData;
    if (!boardViewContext) return null;
    if ("workspaceId" in boardViewContext && "boardId" in boardViewContext) {
      return `${boardViewContext?.workspaceId}-${boardViewContext?.boardId}`;
    }
    return null;
  }, [contextData]);

  // Parse stored IDs
  const [storedWorkspaceId, storedBoardId] = (contextKey ?? lastUsedIds)?.split("-") ?? [null, null];

  const [selectedWorkspace, setSelectedWorkspace] = useState<ResourceType | null>(null);
  // Find default workspace - either from localStorage or first available
  const defaultWorkspace = useMemo(() => {
    if (!workspaces.data?.length) return null;

    if (storedWorkspaceId) {
      const found = workspaces.data.find((ws) => ws.id === storedWorkspaceId);
      if (found) return found;
    }

    return workspaces.data[0];
  }, [workspaces.data, storedWorkspaceId]);

  // Initialize selected workspace when default becomes available
  useEffect(() => {
    if (defaultWorkspace && !selectedWorkspace) {
      setSelectedWorkspace(defaultWorkspace);
    }
  }, [defaultWorkspace, selectedWorkspace]);

  const [boardSearch, setBoardSearch] = useState<string>("");
  const debouncedBoardSearch = useDebounce(boardSearch, 500);

  // Load boards for selected workspace
  const boards = useBoards(selectedWorkspace, withColumns, withSubItemColumns, debouncedBoardSearch || undefined);

  // Apply compatibility filter and sort boards
  const processedBoards = useMemo(() => {
    const rawBoards = boards.boards ?? [];

    if (!boardCompatibilityFilter) {
      // No filter, return boards without compatibility info
      return rawBoards.map((board) => ({ ...board, isCompatible: true }));
    }

    // Apply filter and add compatibility info
    const boardsWithCompatibility = rawBoards.map((board) => ({
      ...board,
      isCompatible: boardCompatibilityFilter(board),
    }));

    // Sort: compatible boards first, then by name
    return boardsWithCompatibility.sort((a, b) => {
      if (a.isCompatible === b.isCompatible) return a.name.localeCompare(b.name);
      return a.isCompatible ? -1 : 1;
    });
  }, [boards.boards, boardCompatibilityFilter]);

  // Find default board - either from localStorage or first compatible
  const defaultBoard = useMemo(() => {
    if (processedBoards.length === 0) return null;

    if (boardCompatibilityFilter) {
      if (storedBoardId) {
        const found = processedBoards.find((board) => board.id === storedBoardId);
        if (found?.isCompatible) return found;
      }
      const firstCompatible = processedBoards.find((board) => board.isCompatible);
      return firstCompatible ?? null;
    }

    if (storedBoardId) {
      const found = processedBoards.find((board) => board.id === storedBoardId);
      if (found) return found;
    }

    return processedBoards[0];
  }, [processedBoards, storedBoardId, boardCompatibilityFilter]);

  const [selectedBoard, setSelectedBoard] = useState<(BoardWithCompatibility & BoardWithColumns) | null>(null);

  // Initialize selected board when default becomes available
  useEffect(() => {
    if (defaultBoard && !selectedBoard) {
      setSelectedBoard(defaultBoard);
    }
  }, [defaultBoard, selectedBoard, setSelectedBoard]);

  // Update localStorage when user makes selections
  useEffect(() => {
    if (selectedWorkspace?.id && selectedBoard?.id) {
      setLastUsedIds(`${selectedWorkspace.id}-${selectedBoard.id}`);
    }
  }, [selectedWorkspace?.id, selectedBoard?.id, setLastUsedIds]);

  useEffect(() => {
    setBoardSearch("");
  }, [selectedWorkspace]);

  // Clear board when workspace changes (but not on initial load)
  useEffect(() => {
    if (selectedWorkspace && selectedBoard) {
      // Check if board belongs to current workspace
      const boardBelongsToWorkspace = processedBoards.some((b) => b.id === selectedBoard.id);
      if (!boardBelongsToWorkspace) {
        setSelectedBoard(null);
      }
    }
  }, [selectedWorkspace, selectedBoard, processedBoards]);

  const items = useItems(selectedBoard, withColumnValues);

  const [search, setSearch] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const { breadcrumbs } = useMondayImportItemsContext();

  // Clear selected items when board changes
  useEffect(() => {
    setSelectedItems([]);
  }, [selectedBoard?.id]);

  useEffect(() => {
    setSearch("");
  }, [breadcrumbs]);

  return {
    workspaces: workspaces.data,
    workspacesInitialLoading: workspaces.isInitialLoading,
    workspacesLoading: workspaces.isLoading,
    loadMoreWorkspaces: workspaces.loadMoreWorkspaces,
    loadMoreBoards: boards.loadMoreBoards,
    selectedWorkspace,
    setSelectedWorkspace,

    boards: processedBoards,
    subItemsBoards: boards.subItemsBoards ?? [],
    boardsLoading: boards.isLoading,
    boardsInitialLoading: boards.isInitialLoading,
    selectedBoard,
    setSelectedBoard,
    setBoardSearch,

    items: items.data,
    itemIdToBoardId: items.itemIdToBoardId,
    itemsInitialLoading: items.isInitialLoading,
    itemsLoading: items.isLoading,
    hasMoreItems: items.hasMore,
    loadMoreItems: items.loadMoreItems,

    search,
    setSearch,

    selectedItems,
    setSelectedItems,
  };
};
